/**
 * Letter-first onboarding — the hook (prototype screen `intro`).
 *
 * The only thing we ask a brand-new user to do. Orion is introduced as
 * available, never as a gate. "Not right now" exists, but nothing else
 * competes with it — declining routes to the Levels screen, not a dead end.
 *
 * "Start my letter" resumes an in-progress draft if one exists, otherwise
 * begins at the recipient screen.
 */
import { useFocusEffect, useNavigation } from '@react-navigation/native';
import React, { useCallback, useState } from 'react';
import {
  View,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  Image,
} from 'react-native';

import { AppText as Text } from '../../components/AppText';
import OrnamentBackground from '../../components/OrnamentBackground';
import { letterToLovedOnesService } from '../../services/letterToLovedOnesInfo';
import { onboardingService } from '../../services/onboarding';
import { createLogger } from '../../utils/logger';
import { ms, s } from '../../utils/responsive';

const logger = createLogger('LetterHook');

export const LetterHookScreen: React.FC = () => {
  const navigation = useNavigation<any>();
  const [starting, setStarting] = useState(false);

  // The hook pitches the FIRST letter. Someone who already saved one
  // belongs on Levels — also covers resume-after-relaunch while the
  // backend's letter-step endpoint isn't deployed yet (it 404s on prod,
  // so onboardingStep can stay stale at 'letter').
  useFocusEffect(
    useCallback(() => {
      let cancelled = false;
      letterToLovedOnesService
        .getAllLettersToLovedOnes()
        .then((letters) => {
          const hasSaved = letters.some((l) => (l.status ?? 'saved') === 'saved');
          if (!cancelled && hasSaved) {
            logger.debug('Saved letter exists — routing past the hook to Levels');
            navigation.replace('LetterLevels');
          }
        })
        .catch((error) => logger.error('Hook letter check failed:', error));
      return () => {
        cancelled = true;
      };
    }, [navigation]),
  );

  const handleStartLetter = async () => {
    if (starting) return;
    logger.debug('Start my letter tapped');
    onboardingService.setLetterStep('letter');
    try {
      setStarting(true);
      // Resume an unfinished draft rather than starting a second one.
      const letters = await letterToLovedOnesService.getAllLettersToLovedOnes();
      const draft = letters.find((l) => l.status === 'draft');
      if (draft) {
        logger.debug('Resuming draft letter:', draft.id);
        navigation.navigate('LetterCanvas', { letterId: draft.id });
        return;
      }
    } catch (error) {
      logger.error('Draft check failed, starting fresh:', error);
    } finally {
      setStarting(false);
    }
    navigation.navigate('LetterRecipient');
  };

  const handleNotRightNow = () => {
    logger.debug('Not right now tapped — routing to Levels');
    onboardingService.setLetterStep('levels');
    navigation.navigate('LetterLevels');
  };

  return (
    <SafeAreaView style={styles.container}>
      <OrnamentBackground />
      <View style={styles.topSection}>
        <Image
          source={require('../../assets/logo.png')}
          style={styles.logo}
          resizeMode="contain"
        />
      </View>

      <View style={styles.content}>
        <Text style={styles.title}>
          The best gift you can leave behind is clarity — and one letter.
        </Text>
        <Text style={styles.subtitle}>
          Not your passwords. Not your paperwork. A letter to one person you
          love, in your own words. It's the part your family will actually
          keep.
        </Text>

        <View style={styles.orionCard}>
          <Image
            source={require('../../assets/icons/chat.png')}
            style={styles.orionIcon}
          />
          <Text style={styles.orionText}>
            Orion can help whenever you want — or stay out of the way.
          </Text>
        </View>
      </View>

      <View style={styles.footer}>
        <TouchableOpacity
          style={styles.startButton}
          onPress={handleStartLetter}
          accessibilityRole="button"
          accessibilityLabel="Start my letter"
          accessibilityHint="Double tap to begin writing a letter to someone you love"
        >
          <Text style={styles.startButtonText}>Start my letter</Text>
        </TouchableOpacity>

        <TouchableOpacity
          style={styles.notNowButton}
          onPress={handleNotRightNow}
          accessibilityRole="button"
          accessibilityLabel="Not right now"
          accessibilityHint="Double tap to see everything else FinalKey offers"
        >
          <Text style={styles.notNowText}>Not right now</Text>
        </TouchableOpacity>
      </View>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#29BFC0',
  },
  topSection: {
    alignItems: 'center',
    paddingTop: ms(24),
  },
  logo: {
    width: s(110),
    height: s(110),
  },
  content: {
    flex: 1,
    paddingHorizontal: ms(28),
    paddingTop: ms(16),
  },
  title: {
    fontSize: ms(26, 0.3),
    fontWeight: 'bold',
    color: '#FFFFFF',
    marginBottom: ms(16),
  },
  subtitle: {
    fontSize: ms(16, 0.3),
    lineHeight: ms(24, 0.3),
    color: '#FFFFFF',
    marginBottom: ms(24),
  },
  orionCard: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
    borderRadius: ms(14),
    padding: ms(14),
    gap: ms(12),
  },
  orionIcon: {
    width: s(32),
    height: s(32),
    tintColor: '#29BFC0',
  },
  orionText: {
    flex: 1,
    fontSize: ms(14, 0.3),
    lineHeight: ms(20, 0.3),
    color: '#1C2C4F',
    fontWeight: '600',
  },
  footer: {
    paddingHorizontal: ms(28),
    paddingBottom: ms(24),
  },
  startButton: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(30),
    paddingVertical: ms(16),
    alignItems: 'center',
  },
  startButtonText: {
    color: '#29BFC0',
    fontSize: ms(18, 0.3),
    fontWeight: '600',
  },
  notNowButton: {
    alignItems: 'center',
    paddingVertical: ms(14),
  },
  notNowText: {
    color: '#FFFFFF',
    fontSize: ms(16, 0.3),
    fontWeight: '600',
    textDecorationLine: 'underline',
  },
});

export default LetterHookScreen;
